import React, { useEffect, useRef, useState } from "react";
import "./ChatAgent.css";
import { createCrmTicket } from "../utils/crmTickets";

const DEFAULT_CHAT_API_URL =
  "https://fnol-frontend-api-650747488835.us-central1.run.app";
const CHAT_TIMEOUT_MS = Number(process.env.REACT_APP_CHAT_TIMEOUT_MS || 45000);
const CHAT_STORAGE_PREFIX = "genpact.agentAssist.chat";
const FNOL_GENERATED_EVENT = "genpact:fnol-generated";
const FNOL_GENERATED_STORAGE_KEY = "genpact.agentAssist.latestFnolGenerated";

const getPublicChatApiUrl = () =>
  (process.env.REACT_APP_CHAT_API_URL || DEFAULT_CHAT_API_URL).replace(/\/$/, "");

const getLocalChatApiPorts = (agent) => {
  const configuredPort = process.env.REACT_APP_CHAT_API_PORT;

  return [
    ...new Set(
      [
        agent?.apiPort ? String(agent.apiPort) : null,
        "5001",
        configuredPort,
        "5000",
      ].filter(Boolean)
    ),
  ];
};

const getChatEndpoints = (agent) => {
  const publicApiUrl = getPublicChatApiUrl();
  const isLocalHost = ["localhost", "127.0.0.1"].includes(
    window.location.hostname
  );

  const shouldTryLocalApi =
    isLocalHost ||
    new URLSearchParams(window.location.search).get("localApi") === "1";

  const endpoints = [];

  if (publicApiUrl) {
    endpoints.push(`${publicApiUrl}/api/chat`);
  }

  if (shouldTryLocalApi) {
    getLocalChatApiPorts(agent).forEach((apiPort) => {
      endpoints.push(`http://localhost:${apiPort}/api/chat`);
    });

    if (isLocalHost) {
      endpoints.push("/api/chat");
    }
  }

  return [...new Set(endpoints)];
};

const fetchWithTimeout = async (url, options, timeoutMs = CHAT_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const getConfigWarning = (agent) => {
  const missingFields = agent?.missingConfigFields || [];

  const hasAppConfigured =
    !missingFields.includes("projectId") &&
    !missingFields.includes("location") &&
    !missingFields.includes("appId");

  if (hasAppConfigured) {
    return "This agent app is configured, but no CES version/deployment is available yet. Deploy it in CX Studio to enable chat.";
  }

  return "Backend config is missing for this agent. Add its app details in agents-config.json to enable chat.";
};

const hasContextPayload = (context) =>
  Boolean(
    context &&
      Object.values(context).some((value) => String(value || "").trim())
  );

const renderMessageText = (text) => {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }

    return part;
  });
};

const normalizeIdValue = (value) => String(value || "").trim();
const CLAIM_ID_PATTERN = /\b(C-\d{6}-\d+)\b/i;
const FNOL_ID_PATTERN = /\b(F-\d{6}-\d+)\b/i;

const normalizeClaimId = (value) => {
  const normalized = normalizeIdValue(value);
  const match = normalized.match(CLAIM_ID_PATTERN);
  return match?.[1] || "";
};

const normalizeFnolId = (value) => {
  const normalized = normalizeIdValue(value);
  const match = normalized.match(FNOL_ID_PATTERN);
  return match?.[1] || "";
};

const findFirstValueByKeys = (value, targetKeys) => {
  if (!value || typeof value !== "object") return "";

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findFirstValueByKeys(item, targetKeys);
      if (found) return found;
    }

    return "";
  }

  for (const [key, itemValue] of Object.entries(value)) {
    if (targetKeys.includes(key.toLowerCase())) {
      const normalized = normalizeIdValue(itemValue);
      if (normalized) return normalized;
    }

    const found = findFirstValueByKeys(itemValue, targetKeys);
    if (found) return found;
  }

  return "";
};

const findFirstMatch = (value, patterns) => {
  const text =
    typeof value === "string" ? value : JSON.stringify(value || {});

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1];
  }

  return "";
};

const extractAutomationIds = (data, replies = []) => {
  const replyText = replies.join("\n");
  const payload = {
    ...data,
    replies,
  };

  const claimId =
    findFirstMatch(replyText, [CLAIM_ID_PATTERN]) ||
    normalizeClaimId(findFirstValueByKeys(payload, ["claim_id", "claimid", "claimnumber"])) ||
    findFirstMatch(payload, [
      /claim[_\s-]*id["'\s:=]+(C-\d{6}-\d+)/i,
      CLAIM_ID_PATTERN,
    ]);

  const fnolId =
    findFirstMatch(replyText, [FNOL_ID_PATTERN]) ||
    normalizeFnolId(findFirstValueByKeys(payload, ["fnol_id", "fnolid", "fnolnumber"])) ||
    findFirstMatch(payload, [
      /fnol[_\s-]*id["'\s:=]+(F-\d{6}-\d+)/i,
      FNOL_ID_PATTERN,
    ]);

  return {
    claimId: normalizeClaimId(claimId),
    fnolId: normalizeFnolId(fnolId),
  };
};

const hasAutomationIds = (ids) => Boolean(ids?.claimId || ids?.fnolId);

const repairStoredAutomationIds = (messages = []) =>
  messages.map((message, index) => {
    if (!message?.automationIds) return message;

    const previousText = messages
      .slice(0, index)
      .map((candidate) => candidate?.text || "")
      .join("\n");
    const currentIds = message.automationIds || {};

    return {
      ...message,
      automationIds: {
        claimId:
          normalizeClaimId(currentIds.claimId) ||
          findFirstMatch(previousText, [CLAIM_ID_PATTERN]),
        fnolId:
          normalizeFnolId(currentIds.fnolId) ||
          findFirstMatch(previousText, [FNOL_ID_PATTERN]),
      },
    };
  });

const notifyFnolGenerated = (ids, details = {}) => {
  if (typeof window === "undefined" || !ids?.fnolId) return;

  const payload = {
    ...ids,
    ...details,
    generatedAt: new Date().toISOString(),
  };

  window.localStorage.setItem(FNOL_GENERATED_STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent(FNOL_GENERATED_EVENT, { detail: payload }));
};

const canShowAutomationIds = (agent) =>
  [
    "insurance-fnol-claims",
    "insurance-fnol-claims-new",
  ].includes(agent?.id);

const shouldRenderRawReplies = (agent) =>
  ["gmail-fnol-intake-agent", "fnol-intake-agent"].includes(agent?.id);

const isTransientChatError = (message) =>
  message?.sender === "bot" &&
  message?.text === "Unable to reach the selected agent right now. Please try again.";

const renderAutomationIds = (ids) => (
  <div className="automation-id-card">
    <span>Automation completed</span>
    {ids.claimId && (
      <strong>
        Claim ID: <code>{ids.claimId}</code>
      </strong>
    )}
    {ids.fnolId && (
      <strong>
        FNOL ID: <code>{ids.fnolId}</code>
      </strong>
    )}
  </div>
);

const getChatStorageKey = (agent, sessionKey = "") =>
  `${CHAT_STORAGE_PREFIX}.${agent?.id || "default"}.${sessionKey || "default"}`;

const loadStoredChat = (agent, sessionKey) => {
  if (!agent) return { messages: [], sessionId: null };

  try {
    const stored = JSON.parse(
      window.localStorage.getItem(getChatStorageKey(agent, sessionKey)) || "null"
    );

    const storedMessages = Array.isArray(stored?.messages) ? stored.messages : [];
    const restorableMessages = repairStoredAutomationIds(
      storedMessages.filter((message) => !isTransientChatError(message))
    );

    return {
      messages: canShowAutomationIds(agent)
        ? restorableMessages
        : restorableMessages.filter((message) => !message?.automationIds),
      sessionId: stored?.sessionId || null,
    };
  } catch {
    return { messages: [], sessionId: null };
  }
};

const saveStoredChat = (agent, sessionKey, messages, sessionId) => {
  if (!agent) return;

  window.localStorage.setItem(
    getChatStorageKey(agent, sessionKey),
    JSON.stringify({
      messages,
      sessionId: sessionId || null,
      savedAt: new Date().toISOString(),
    })
  );
};

const ChatAgent = ({
  agent,
  hideTitle = false,
  context = null,
  sessionKey = "",
}) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const sessionIdRef = useRef(null);
  const bottomRef = useRef(null);
  const skipNextStorageSaveRef = useRef(false);

  const canChat = Boolean(agent?.backendReady);

  useEffect(() => {
    if (!agent) return;

    const storedChat = loadStoredChat(agent, sessionKey);
    skipNextStorageSaveRef.current = true;
    sessionIdRef.current = storedChat.sessionId;
    setMessages(storedChat.messages);
  }, [agent, sessionKey]);

  useEffect(() => {
    if (!agent) return;

    if (skipNextStorageSaveRef.current) {
      skipNextStorageSaveRef.current = false;
      return;
    }

    saveStoredChat(agent, sessionKey, messages, sessionIdRef.current);
  }, [agent, sessionKey, messages]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();

    if (!agent || !canChat || !inputMessage.trim() || isSending) {
      return;
    }

    const text = inputMessage.trim();

    setMessages((prev) => [...prev, { sender: "user", text }]);
    setInputMessage("");
    setIsSending(true);

    try {
      let data = null;
      let lastError = null;

      for (const endpoint of getChatEndpoints(agent)) {
        try {
          const response = await fetchWithTimeout(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              agentId: agent.id,
              message: text,
              sessionId: sessionIdRef.current,
              context: hasContextPayload(context) ? context : undefined,
            }),
          });

          if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            throw new Error(errorBody.error || "Agent request failed");
          }

          data = await response.json();
          break;
        } catch (error) {
          lastError = error;
        }
      }

      if (!data) {
        throw lastError || new Error("Agent request failed");
      }

      sessionIdRef.current = data.sessionId || sessionIdRef.current;

      const replies = Array.isArray(data.replies) ? data.replies : [];
      const automationIds = extractAutomationIds(data, replies);

      if (replies.length) {
        replies.forEach((reply) => {
          setMessages((prev) => [...prev, { sender: "bot", text: reply }]);
        });
      } else {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: "I received the request, but no response text came back from the agent.",
          },
        ]);
      }

      if (canShowAutomationIds(agent) && hasAutomationIds(automationIds)) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: "",
            automationIds,
          },
        ]);
      }

      notifyFnolGenerated(automationIds, {
        chatStorageKey: getChatStorageKey(agent, sessionKey),
        sessionId: sessionIdRef.current,
      });

      try {
        await createCrmTicket({
          agent,
          customerMessage: text,
          agentReplies: replies,
        });
      } catch {
        // Chat succeeded; CRM ticket persistence should never hide the agent response.
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "Unable to reach the selected agent right now. Please try again.",
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="agent-chat-layout">
      <section className="chat-container">
        <h2 className={hideTitle ? "chat-title-hidden" : ""}>
          {agent ? agent.name : "Select an agent"}
        </h2>

        {agent && !canChat && (
          <div className="config-warning" role="status">
            {getConfigWarning(agent)}
          </div>
        )}

        <div className="messages-container">
          {messages.map((msg, index) => (
            <div
              key={`${msg.sender}-${index}`}
              className={
                msg.sender === "user"
                  ? "message user-message"
                  : "message bot-message"
              }
            >
              {msg.automationIds
                ? renderAutomationIds(msg.automationIds)
                : shouldRenderRawReplies(agent)
                  ? msg.text
                  : renderMessageText(msg.text)}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <form className="input-container" onSubmit={handleSend}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Type your message..."
            disabled={!agent || !canChat || isSending}
          />

          <button type="submit" disabled={!agent || !canChat || isSending}>
            {isSending ? "Sending..." : "Send"}
          </button>
        </form>
      </section>
    </div>
  );
};

export default ChatAgent;
